// Website portfolio (client work plus clearly-labelled template sites), kept separate from the owned
// SaaS products in src/data/products.ts (which appear on /products).
// Each entry powers the /work page, the homepage "Recent client work" strip,
// and the showcases on relevant service + industry pages.

export interface ClientWork {
  slug: string;
  client: string;
  /** Full industry label shown under the client name. */
  industry: string;
  /** Short chip/tag shown over the screenshot. */
  industryTag: string;
  location: string;
  /** One-line "what was built" summary. */
  summary: string;
  /** Concrete things built, rendered as pills. */
  highlights: string[];
  liveUrl: string;
  image: string;
  imageAlt: string;
  /**
   * 'template' marks demonstration/template sites (fictional brand, placeholder
   * contact details) that are shown as examples of our work but are NOT
   * commissioned client projects. Omitted = no claim either way.
   */
  kind?: 'client' | 'template';
}

export const clientWork: ClientWork[] = [
  {
    slug: 'prime-renovations',
    client: 'Prime Renovations',
    industry: 'Renovation & Construction',
    industryTag: 'Construction & Trades',
    location: 'Edmonton, AB',
    summary:
      'A premium, conversion-focused website with fixed-quote lead capture, detailed service pages, a 4-step process flow, trust badges, and click-to-call.',
    highlights: [
      'Fixed-quote lead capture',
      'Service pages',
      '4-step process flow',
      'Trust badges',
      'Click-to-call',
    ],
    liveUrl: 'https://www.primerenovations.info',
    image: '/work/prime-renovations.jpg',
    imageAlt: 'Prime Renovations — renovation and construction website hero (Edmonton)',
  },
  {
    slug: 'trendz-furniture',
    client: 'Trendz Furniture',
    industry: 'Retail / Furniture Showroom',
    industryTag: 'Retail / E-commerce',
    location: 'Edmonton, AB',
    summary:
      'A luxury e-commerce showroom site with room-based product collections, a new-arrivals grid, cart, financing and delivery info, a showroom locator, Google reviews, and WhatsApp/call widgets.',
    highlights: [
      'Room-based collections',
      'New-arrivals grid + cart',
      'Financing & delivery info',
      'Showroom locator',
      'Google reviews integration',
      'WhatsApp & call widgets',
    ],
    liveUrl: 'https://www.trendzfurniture.ca',
    image: '/work/trendz-furniture.jpg',
    imageAlt: 'Trendz Furniture — luxury furniture showroom e-commerce website hero (Edmonton)',
  },
  {
    slug: 'gateway-furniture',
    client: 'Gateway Furniture',
    industry: 'Retail / Furniture Showroom',
    industryTag: 'Retail / Furniture',
    location: 'Edmonton, AB',
    summary:
      'Built a modern furniture showroom website with category collections (bedroom, sofa, living room, dining), a new-arrivals showcase, promotions, a browsable catalogue, financing and free Edmonton delivery details, warranty info, customer reviews, and click-to-call.',
    highlights: [
      'Category collections',
      'New arrivals & promotions',
      'Product catalogue',
      'Financing & delivery info',
      'Customer reviews',
      'Click-to-call',
    ],
    liveUrl: 'https://www.gatewayfurniture.ca',
    image: '/work/gateway-furniture.jpg',
    imageAlt: 'Gateway Furniture — modern furniture showroom website hero (Edmonton)',
  },
  {
    slug: 'aura-charters',
    client: 'AURA Yacht Charters',
    industry: 'Luxury Travel / Yacht Charters',
    industryTag: 'Hospitality / Travel',
    location: 'Vancouver, BC',
    summary:
      'Built a premium luxury yacht-charter website with a fleet showcase, curated experiences and destinations, a membership tier, and a booking enquiry search (location, date, guests) — designed to turn high-end visitors into charter bookings.',
    highlights: [
      'Fleet showcase',
      'Experiences & destinations',
      'Booking enquiry search',
      'Membership tier',
      'Luxury responsive design',
      'Local SEO + schema',
    ],
    liveUrl: 'https://aura-charters.vercel.app',
    image: '/work/aura-charters.jpg',
    imageAlt: 'AURA Yacht Charters — luxury yacht charter website hero (Vancouver)',
  },
  {
    slug: 'prime-auto-glass',
    client: 'Prime Auto Glass',
    industry: 'Auto Glass / Windshield Replacement',
    industryTag: 'Automotive',
    location: 'BC & Alberta',
    summary:
      'Built a premium mobile windshield-replacement website with online quote requests, direct insurance billing, ADAS recalibration, mobile-service booking, a lifetime-warranty offer, and click-to-call — serving BC’s Lower Mainland and Greater Edmonton.',
    highlights: [
      'Online quote requests',
      'Direct insurance billing',
      'Mobile service booking',
      'ADAS calibration',
      'Lifetime warranty offer',
      'Click-to-call',
    ],
    liveUrl: 'https://prime-auto-glass.vercel.app',
    image: '/work/prime-auto-glass.jpg',
    imageAlt: 'Prime Auto Glass — mobile windshield replacement website hero (BC & Alberta)',
  },
  {
    slug: 'aqua-dental-loft',
    client: 'Aqua Dental Loft',
    industry: 'Dental / Healthcare',
    industryTag: 'Healthcare / Dental',
    location: 'Boca Raton, FL',
    summary:
      'Built a modern dental-practice website with online appointment booking, full service pages (general, cosmetic, children’s, Invisalign, veneers, emergency), doctor and team bios, testimonials, an insurance/new-patient flow, and click-to-call.',
    highlights: [
      'Online appointment booking',
      'Service pages',
      'Doctor & team bios',
      'Testimonials',
      'Insurance & new-patient flow',
      'Click-to-call',
    ],
    liveUrl: 'https://aqua-dental-loft.vercel.app',
    image: '/work/aqua-dental-loft.jpg',
    imageAlt: 'Aqua Dental Loft — dental practice website hero (Boca Raton, FL)',
  },
  {
    slug: 'anchora-insurance',
    client: 'Anchora Insurance',
    industry: 'Life Insurance / Financial Services',
    industryTag: 'Insurance / Finance',
    location: 'United States',
    summary:
      'Built a clean life-insurance advisory website with coverage pages (term, whole, indexed universal life, long-term care, annuities), educational resources, a how-it-works flow, an FAQ, and a schedule-a-call booking — for families and business owners.',
    highlights: [
      'Coverage / service pages',
      'Educational resources',
      'How-it-works flow',
      'FAQ',
      'Schedule-a-call booking',
      'Click-to-call',
    ],
    liveUrl: 'https://anchora-insurance.vercel.app',
    image: '/work/anchora-insurance.jpg',
    imageAlt: 'Anchora Insurance — life insurance advisory website hero',
  },
  {
    slug: 'northcraft',
    client: 'Northcraft',
    industry: 'Design-Build / Construction',
    industryTag: 'Construction & Trades',
    location: 'Toronto, ON',
    summary:
      'Built a striking design-build construction website with consultation booking, detailed service pages (custom homes, additions, laneway homes, renovations, roofing, concrete), a project gallery with before/after and case studies, a process flow, testimonials, and an online estimator.',
    highlights: [
      'Consultation booking',
      'Service pages',
      'Project gallery + before/after',
      'Case studies & testimonials',
      'Online estimator',
      'Financing info',
    ],
    liveUrl: 'https://northcraft-theta.vercel.app',
    image: '/work/northcraft.jpg',
    imageAlt: 'Northcraft — design-build & renovation firm website hero (Toronto)',
  },
  {
    slug: 'neon-oiran',
    client: 'Neon Oiran',
    industry: 'AAA Cyberpunk Action RPG',
    industryTag: 'Gaming / Entertainment',
    location: 'Worldwide',
    summary:
      'Built an immersive AAA game marketing website with a cinematic neon hero, trailer, deep lore and world-building, augments, missions, armory and factions sections, and multi-platform wishlist/buy links (PC, PlayStation, Xbox & Steam).',
    highlights: [
      'Cinematic hero + trailer',
      'Lore & world-building',
      'Missions & armory',
      'Factions',
      'Wishlist / buy links',
      'Multi-platform (PC, PS, Xbox, Steam)',
    ],
    liveUrl: 'https://neon-oiran.vercel.app',
    image: '/work/neon-oiran.jpg',
    imageAlt: 'Neon Oiran — AAA cyberpunk action RPG marketing website hero',
  },
  {
    slug: 'atelier-forma',
    client: 'Atelier Forma',
    industry: 'Interior Architecture / Design Studio',
    industryTag: 'Design / Architecture',
    location: 'Global',
    summary:
      'Built an editorial portfolio website for a global interior architecture studio — a projects gallery, a philosophy and materials narrative, a journal, and a refined contact flow, showcasing timeless residential, hospitality, cultural, and wellness interiors.',
    highlights: [
      'Projects portfolio gallery',
      'Philosophy & materials pages',
      'Journal / editorial',
      'Refined contact flow',
      'Timeless editorial design',
    ],
    liveUrl: 'https://atelier-forma-site.vercel.app',
    image: '/work/atelier-forma.jpg',
    imageAlt: 'Atelier Forma — interior architecture studio website hero',
  },
  {
    slug: 'northcrest-homes',
    client: 'Northcrest Homes',
    industry: 'Custom Home Builder / Construction',
    industryTag: 'Construction & Trades',
    location: 'Calgary, AB',
    summary:
      'Built a conversion-focused custom home builder website with a 2-minute cost estimator, feasibility-consult booking, a process flow, a homes portfolio gallery, communities pages, and an FAQ.',
    highlights: [
      '2-minute cost estimator',
      'Consultation booking',
      'Process flow',
      'Homes portfolio gallery',
      'Communities pages',
      'FAQ',
    ],
    liveUrl: 'https://northcrest-home-builders.vercel.app',
    image: '/work/northcrest-homes.jpg',
    imageAlt: 'Northcrest Homes — custom home builder website hero (Calgary)',
  },
  {
    slug: 'lumiere-aesthetics',
    client: 'Lumière Aesthetics',
    industry: 'Med Spa / Aesthetics Clinic',
    industryTag: 'Beauty / Aesthetics',
    location: 'Toronto, ON',
    summary:
      'Built a med spa and aesthetic clinic website with treatment pages (Botox, filler, laser, skin), a before-and-after gallery, transparent pricing, a why-us section, financing info, and consultation booking.',
    highlights: [
      'Treatment pages',
      'Before & after gallery',
      'Transparent pricing',
      'Consultation booking',
      'Financing info',
      'Click-to-call',
    ],
    liveUrl: 'https://brainfy-clinic-kit.vercel.app',
    image: '/work/lumiere-aesthetics.jpg',
    imageAlt: 'Lumière Aesthetics — med spa and aesthetic clinic website hero (Toronto)',
    kind: 'template',
  },
  {
    slug: 'whitfield-cho-law',
    client: 'Whitfield & Cho LLP',
    industry: 'Law Firm / Legal Services',
    industryTag: 'Legal',
    location: 'Toronto, ON',
    summary:
      'Built a full-service law firm website with practice-area pages (personal injury, family, criminal, employment, real estate, business), a team/attorney section, results and insights, a client login, and 24/7 free-consultation booking.',
    highlights: [
      'Practice-area pages',
      'Team / attorney bios',
      'Results & insights',
      'Client login',
      '24/7 consultation booking',
      'Click-to-call',
    ],
    liveUrl: 'https://brainfy-lawfirm-template.vercel.app',
    image: '/work/whitfield-cho-law.jpg',
    imageAlt: 'Whitfield & Cho LLP — Toronto law firm website hero',
    kind: 'template',
  },
  {
    slug: 'sterling-vale-realestate',
    client: 'Sterling & Vale',
    industry: 'Real Estate',
    industryTag: 'Real Estate',
    location: 'Toronto, ON',
    summary:
      'Built a luxury real-estate website with full GTA listing search, an instant home-valuation tool, buyer and seller flows, community pages, and agent contact — powered by RealtorDesk AI.',
    highlights: [
      'GTA listing search',
      'Instant home valuation',
      'Buy & sell flows',
      'Community pages',
      'RealtorDesk AI powered',
      'Click-to-call',
    ],
    liveUrl: 'https://brainfy-real-estate.vercel.app',
    image: '/work/sterling-vale-realestate.jpg',
    imageAlt: 'Sterling & Vale — luxury real estate website hero (Toronto GTA)',
    kind: 'template',
  },
];

export const getClientWorkBySlugs = (slugs: string[]): ClientWork[] =>
  slugs
    .map((s) => clientWork.find((w) => w.slug === s))
    .filter((w): w is ClientWork => Boolean(w));
